/* The Written Hug - message cap guard.
 *
 * WHY THIS EXISTS: order #1015 paid $20 with a 623-character message against a
 * 500-character cap, and the printer then refused it. The customer had already
 * been charged. Two separate holes caused that:
 *
 *   1. `maxlength` only constrains TYPING and PASTING. The AI draft tool assigns
 *      textarea.value directly, which walks straight past the attribute.
 *   2. Nothing stopped Add to Cart, so an over-length message became a paid order
 *      that could never be fulfilled.
 *
 * This file closes both. It is deliberately standalone rather than surgery on
 * twh-delivery.js: it attaches late, overrides nothing, and if any element it
 * expects is missing it simply does nothing and leaves the page exactly as it was.
 *
 * It measures what the PIPELINE will measure, not what the browser shows:
 *   - Shopify stores the textarea's \n as \r\n, so every line break costs 2 chars.
 *   - worker.js appends the signoff ("Sign as", or the anonymous phrase) and counts
 *     that too, so we count it here as well.
 * Pipeline totals live in worker.js PAGE_MAX_CHARS: 1100 for a folded card across
 * both inside panels, 1600 for the A4 letter. The UI cap sits below that on purpose;
 * the difference is margin for the counting above, not headroom to spend.
 */
(function () {
  "use strict";

  var msg = document.getElementById("twh-message");
  var per = document.querySelector(".twh-personalize");
  if (!msg || !per) return;

  var CFG = window.TWH_DELIVERY || {};
  var CAP = CFG.cap || 500;                       // what the buyer may type
  var ISLETTER = !!CFG.isLetter;
  var PIPE_TOTAL = ISLETTER ? 1600 : 1100;        // worker.js PAGE_MAX_CHARS
  var PANEL = 550;                                // one inside panel of a folded card

  /* ---------- what the pipeline will actually see ---------- */

  function storedBody() {
    // Shopify normalises the textarea's line breaks to CRLF on submit.
    return String(msg.value || "").replace(/\r\n/g, "\n").replace(/\n/g, "\r\n").trim();
  }

  function signoff() {
    var anon = document.querySelector('input[name="properties[Anonymous]"]');
    if (anon && anon.checked) return "A friend who cares";
    var sg = document.getElementById("twh-signas");
    return sg ? String(sg.value || "").trim() : "";
  }

  function effLen() {
    var b = storedBody();
    if (!b) return 0;
    var s = signoff();
    return b.length + (s ? s.length + 2 : 0);
  }

  function panelsNeeded() {
    if (ISLETTER) return 1;
    return effLen() > PANEL ? 2 : 1;
  }

  /* ---------- 1. clamp every write, not just typed ones ---------- */

  (function clampWrites() {
    var d = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, "value");
    if (!d || !d.set || !d.get) return;
    try {
      Object.defineProperty(msg, "value", {
        configurable: true,
        enumerable: true,
        get: function () { return d.get.call(this); },
        set: function (v) {
          v = String(v == null ? "" : v);
          if (v.length > CAP) v = v.slice(0, CAP);
          d.set.call(this, v);
          try { refresh(); } catch (e) {}
        }
      });
    } catch (e) { /* older browser: maxlength still covers typing and pasting */ }
  })();

  /* ---------- 2. the notice ---------- */

  var notice = document.createElement("p");
  notice.className = "twh-cap-notice";
  notice.id = "twh-cap-notice";
  notice.setAttribute("role", "status");
  notice.setAttribute("aria-live", "polite");
  notice.style.cssText =
    "margin:.4rem 0 0;font-size:.82rem;line-height:1.45;color:#6B6154;";
  msg.insertAdjacentElement("afterend", notice);

  function setNotice(state, text) {
    notice.textContent = text || "";
    if (state === "over") {
      notice.style.color = "#B3341C";
      notice.style.fontWeight = "600";
    } else {
      notice.style.color = "#6B6154";
      notice.style.fontWeight = "400";
    }
  }

  /* ---------- 3. the gate ---------- */

  function buttons() {
    return document.querySelectorAll(
      ".add-to-cart-button, button[name='add'], .sticky-add-to-cart__button"
    );
  }

  var blocked = false;

  function applyGate() {
    var over = effLen() > PIPE_TOTAL;
    blocked = over;
    var list = buttons();
    for (var i = 0; i < list.length; i++) {
      var b = list[i];
      if (over) {
        if (!b.hasAttribute("data-twh-cap-was")) {
          b.setAttribute("data-twh-cap-was", b.disabled ? "1" : "0");
        }
        b.disabled = true;
        b.setAttribute("aria-disabled", "true");
        b.style.opacity = "0.55";
        b.style.cursor = "not-allowed";
      } else if (b.hasAttribute("data-twh-cap-was")) {
        if (b.getAttribute("data-twh-cap-was") === "0") b.disabled = false;
        b.removeAttribute("data-twh-cap-was");
        b.removeAttribute("aria-disabled");
        b.style.opacity = "";
        b.style.cursor = "";
      }
    }
    try {
      msg.setCustomValidity(
        over ? "Your message is too long to fit on this card. Please shorten it." : ""
      );
    } catch (e) {}
  }

  /* Belt and braces. Horizon's Add to Cart uses its own click handler rather than a
   * plain form submit, so `disabled` alone is not enough to trust. Block in the
   * capture phase, before anything else sees the event. */
  function veto(e) {
    if (!blocked) return;
    var t = e.target && e.target.closest
      ? e.target.closest(".add-to-cart-button, button[name='add'], .sticky-add-to-cart__button")
      : null;
    if (!t && e.type !== "submit") return;
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    refresh();
    try { msg.focus(); } catch (err) {}
    notice.scrollIntoView({ block: "center", behavior: "smooth" });
  }
  document.addEventListener("click", veto, true);
  document.addEventListener("submit", veto, true);

  /* ---------- 4. keep everything in step ---------- */

  function refresh() {
    var n = effLen();
    if (n > PIPE_TOTAL) {
      setNotice(
        "over",
        "Your message is " + n + " characters. This card holds about " + PIPE_TOTAL +
          ". Please shorten it by about " + (n - PIPE_TOTAL) +
          " to continue, or send it as a letter instead."
      );
    } else if (panelsNeeded() === 2) {
      setNotice(
        "two",
        "This runs across both inside pages of the card, written in real ink on the left page and continuing on the right."
      );
    } else {
      setNotice("ok", "");
    }
    applyGate();
  }

  ["input", "change", "keyup", "paste", "cut"].forEach(function (ev) {
    msg.addEventListener(ev, function () { setTimeout(refresh, 0); });
  });
  /* "Sign as" shipped with NO maxlength, and worker.js appends it to the message
   * before it measures. An unbounded name could therefore push a perfectly legal
   * message past the printable total. Nobody signs a card with a paragraph. */
  var sg = document.getElementById("twh-signas");
  if (sg) {
    if (!sg.getAttribute("maxlength")) sg.maxLength = 60;
    sg.addEventListener("input", refresh);
  }
  var anon = document.querySelector('input[name="properties[Anonymous]"]');
  if (anon) anon.addEventListener("change", refresh);

  /* The AI draft tool and any other scripted writer are covered by the value clamp
   * above, but some paths set the value before this file runs. Sweep briefly on load
   * so the gate is never stale on first paint. */
  var ticks = 0;
  var poll = setInterval(function () {
    refresh();
    if (++ticks > 20) clearInterval(poll);
  }, 250);

  refresh();
})();
